import { IActionWithParams } from '../redux/apiMiddleware'
import { ILoadingData } from '../redux/types'

/** A loading slice plus the key of the request it was last asked for. */
export type IRequestScopedState<T> = ILoadingData<T> & {
  requestedKey: string | null
  /** The one request whose reply is taken; an optimistic write claims it to drop older reads. */
  latestRequestId?: number
}

interface IRequestScopedOptions<T, P, D> {
  /** Which request an action belongs to, derived from its params. */
  keyOf: (params: P | undefined) => string | null
  /** Re-requesting the key already loaded keeps its data on screen instead of reloading. */
  keepDataOnSameKey?: boolean
  /** Shapes a reply into the stored data; stores it as is by default. */
  toData?: (data: D | undefined) => T | null
  isInitiallyLoading?: boolean
  /** Every other action, e.g. websocket pushes that patch the loaded data. */
  extraCases?: (
    state: IRequestScopedState<T>,
    action: IActionWithParams<P, D>,
  ) => IRequestScopedState<T>
}

// Without `toData`, D is T (its default), so the reply is already the stored shape.
const storeAsIs = <T, D>(data: D | undefined) => (data ?? null) as unknown as T | null

/**
 * A slice that only takes the reply to its latest request. `apiMiddleware` can't
 * cancel, so an earlier request's reply can land last; any reply whose key isn't
 * `requestedKey`, or that isn't `latestRequestId`, is dropped. A non-quiet request
 * clears `data`, so a failure shows the empty state for the new key rather than the
 * old key's data. `params.isQuiet` makes a request keep the current data and skip
 * the loader.
 */
export const createRequestScopedReducer = <T, P = unknown, D = T>(
  [requestType, successType, failureType]: string[],
  {
    keyOf,
    keepDataOnSameKey = false,
    toData = storeAsIs,
    isInitiallyLoading = false,
    extraCases,
  }: IRequestScopedOptions<T, P, D>,
) => {
  const initialState: IRequestScopedState<T> = {
    isLoading: isInitiallyLoading,
    isSaving: false,
    isSaved: false,
    data: null,
    requestedKey: null,
  }

  const isQuietRequest = (
    state: IRequestScopedState<T>,
    params: P | undefined,
    key: string | null,
  ) =>
    Boolean((params as { isQuiet?: boolean } | undefined)?.isQuiet) ||
    (keepDataOnSameKey && state.data !== null && state.requestedKey === key)

  const isCurrentReply = (state: IRequestScopedState<T>, action: IActionWithParams<P, D>) =>
    keyOf(action.params) === state.requestedKey &&
    (action.requestId === undefined ||
      state.latestRequestId === undefined ||
      action.requestId === state.latestRequestId)

  return (
    state: IRequestScopedState<T> = initialState,
    action: IActionWithParams<P, D>,
  ): IRequestScopedState<T> => {
    switch (action.type) {
      case requestType: {
        const requestedKey = keyOf(action.params)
        const latestRequestId = action.requestId
        return isQuietRequest(state, action.params, requestedKey)
          ? { ...state, requestedKey, latestRequestId }
          : { ...state, isLoading: true, data: null, requestedKey, latestRequestId }
      }
      case successType:
        return isCurrentReply(state, action)
          ? { ...state, isLoading: false, data: toData(action.data) }
          : state
      case failureType:
        return isCurrentReply(state, action) ? { ...state, isLoading: false } : state
      default:
        return extraCases ? extraCases(state, action) : state
    }
  }
}
